import React, { Component } from 'react';
import {
    Alert,
    Avatar,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Progress,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Switch,
    Table,
    Tag,
    Tooltip,
    Upload,
    notification
} from 'antd';
import {
    ClockCircleOutlined,
    EyeOutlined,
    LoginOutlined,
    MessageOutlined,
    PictureOutlined,
    PlusOutlined,
    ReloadOutlined,
    StopOutlined,
    TeamOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { fetchData } from '../../../lib/apis';
import { AccountMetaData } from '../../../config/settings';
import ZaloConnect from '../channel/ZaloConnect';
import './index.css';

const STATUS = {
    scheduled: { color: 'blue', text: 'Đã lên lịch' },
    running: { color: 'processing', text: 'Đang gửi' },
    completed: { color: 'success', text: 'Hoàn tất' },
    partially_completed: { color: 'warning', text: 'Hoàn tất một phần' },
    failed: { color: 'error', text: 'Thất bại' },
    cancelled: { color: 'default', text: 'Đã hủy' }
};

const ACTION_STATUS = {
    skipped: { color: 'default', text: 'Không thực hiện' },
    pending: { color: 'blue', text: 'Đang chờ' },
    sent: { color: 'success', text: 'Thành công' },
    already_friend: { color: 'cyan', text: 'Đã là bạn bè' },
    failed: { color: 'error', text: 'Thất bại' }
};

export default class ZaloCampaignView extends Component {
    formRef = React.createRef();

    state = {
        loading: false,
        creating: false,
        campaigns: [],
        total: 0,
        page: 1,
        limit: 20,
        keyword: '',
        status: '',
        channels: [],
        selectedChannelId: '',
        selectedChannelSession: null,
        channelSessionChecking: false,
        reconnectVisible: false,
        reconnectChannel: null,
        recipientSource: 'phones',
        zaloFriends: [],
        friendsLoading: false,
        sendAllFriends: true,
        imageFiles: [],
        formVisible: false,
        previewLoading: false,
        phonePreview: null,
        scheduleEnabled: false,
        friendEnabled: false,
        detailVisible: false,
        detailLoading: false,
        selectedCampaign: null,
        targets: [],
        targetTotal: 0,
        targetPage: 1
    };

    componentDidMount() {
        this.loadCampaigns();
        this.loadChannels();
        this.refreshTimer = setInterval(() => {
            const hasActiveCampaign = this.state.campaigns.some(item =>
                ['scheduled', 'running'].includes(item.status)
            );
            if (hasActiveCampaign || this.state.detailVisible) this.loadCampaigns(false);
            if (this.state.detailVisible && this.state.selectedCampaign) this.loadTargets(false);
        }, 5000);
    }

    componentWillUnmount() {
        if (this.refreshTimer) clearInterval(this.refreshTimer);
        if (this.searchTimer) clearTimeout(this.searchTimer);
    }

    loadChannels = async () => {
        try {
            const response = await fetchData({
                url: 'api/v1/channel/list?channel_type=zalo&limit=100&page=1'
            });
            await new Promise(resolve => this.setState({ channels: response.data || [] }, resolve));
            return response.data || [];
        } catch (err) {
            notification.error({ message: 'Không tải được danh sách tài khoản Zalo' });
        }
    };

    getChannel = channelId => this.state.channels.find(channel => String(channel._id) === String(channelId));

    checkSelectedChannel = async channelId => {
        if (!channelId) {
            this.setState({ selectedChannelSession: null, channelSessionChecking: false });
            return;
        }
        this.setState({ channelSessionChecking: true, selectedChannelSession: null });
        try {
            const response = await fetchData({
                url: `api/v1/channel/zalo/${channelId}/check-session`,
                method: 'post',
                data: {}
            });
            const session = response.data || { status: 'error', message: 'Không thể kiểm tra phiên Zalo' };
            this.setState(prev => ({
                channelSessionChecking: false,
                selectedChannelSession: session,
                channels: prev.channels.map(channel => String(channel._id) === String(channelId)
                    ? {
                        ...channel,
                        zalo_credentials: {
                            ...(channel.zalo_credentials || {}),
                            session_status: session.status,
                            last_error: session.error || ''
                        }
                    }
                    : channel)
            }));
            if (session.status !== 'active') {
                notification.warning({
                    message: session.status === 'expired' ? 'Phiên Zalo đã hết hạn' : 'Không thể xác thực tài khoản Zalo',
                    description: session.status === 'expired'
                        ? 'Vui lòng đăng nhập lại trước khi tạo chiến dịch.'
                        : (session.error || session.message)
                });
            }
            return session;
        } catch (err) {
            const session = {
                status: 'error',
                message: 'Không thể kiểm tra phiên Zalo',
                error: err.message || 'Vui lòng thử lại'
            };
            this.setState({ channelSessionChecking: false, selectedChannelSession: session });
            notification.error({ message: session.message, description: session.error });
            return session;
        }
    };

    openReconnect = channel => {
        if (!channel) return;
        this.setState({ reconnectVisible: true, reconnectChannel: channel });
    };

    handleReconnectSuccess = async () => {
        const channelId = this.state.reconnectChannel && this.state.reconnectChannel._id;
        this.setState({ reconnectVisible: false, reconnectChannel: null });
        await this.loadChannels();
        if (channelId && String(this.state.selectedChannelId) === String(channelId)) {
            await this.checkSelectedChannel(channelId);
        }
    };

    loadFriends = async channelId => {
        if (!channelId) {
            this.setState({ zaloFriends: [] });
            return;
        }
        this.setState({ friendsLoading: true });
        try {
            const response = await fetchData({
                url: `api/v1/channel/zalo/${channelId}/friends?page=1&limit=1000`
            });
            this.setState({ zaloFriends: response.data || [], friendsLoading: false });
        } catch (err) {
            this.setState({ zaloFriends: [], friendsLoading: false });
            notification.error({ message: 'Không tải được danh sách bạn bè Zalo' });
        }
    };

    changeRecipientSource = event => {
        const recipientSource = event.target.value;
        this.setState({
            recipientSource,
            phonePreview: null,
            friendEnabled: recipientSource === 'phones' ? this.state.friendEnabled : false
        });
        if (this.formRef.current) {
            this.formRef.current.setFieldsValue({ phones: '', friend_ids: [] });
        }
        if (recipientSource === 'friends') this.loadFriends(this.state.selectedChannelId);
    };

    loadCampaigns = async (showLoading = true) => {
        const { page, limit, keyword, status } = this.state;
        if (showLoading) this.setState({ loading: true });
        try {
            const params = new URLSearchParams({ page, limit });
            if (keyword) params.set('keyword', keyword);
            if (status) params.set('status', status);
            const response = await fetchData({ url: `api/v1/zalo-campaigns?${params.toString()}` });
            this.setState({
                campaigns: response.data || [],
                total: response.total || 0,
                loading: false
            });
        } catch (err) {
            this.setState({ loading: false });
            notification.error({ message: 'Không tải được danh sách chiến dịch' });
        }
    };

    previewPhones = async () => {
        const phones = this.formRef.current && this.formRef.current.getFieldValue('phones');
        if (!phones || !phones.trim()) {
            this.setState({ phonePreview: null });
            return;
        }
        this.setState({ previewLoading: true });
        try {
            const response = await fetchData({
                url: 'api/v1/zalo-campaigns/preview-phones',
                method: 'post',
                data: { phones }
            });
            this.setState({ phonePreview: response.data, previewLoading: false });
        } catch (err) {
            this.setState({ previewLoading: false });
        }
    };

    handlePhonesChange = () => {
        if (this.previewTimer) clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(this.previewPhones, 400);
    };

    closeCampaignForm = () => {
        this.setState({
            formVisible: false,
            phonePreview: null,
            scheduleEnabled: false,
            friendEnabled: false,
            recipientSource: 'phones',
            selectedChannelId: '',
            selectedChannelSession: null,
            channelSessionChecking: false,
            zaloFriends: [],
            sendAllFriends: true,
            imageFiles: []
        }, () => {
            if (this.formRef.current) this.formRef.current.resetFields();
        });
    };

    createCampaign = async values => {
        const {
            scheduleEnabled,
            friendEnabled,
            phonePreview,
            recipientSource,
            sendAllFriends,
            imageFiles
        } = this.state;
        if (this.state.channelSessionChecking) {
            notification.warning({ message: 'Đang kiểm tra phiên Zalo, vui lòng chờ' });
            return;
        }
        if (!this.state.selectedChannelSession || this.state.selectedChannelSession.status !== 'active') {
            notification.warning({
                message: 'Tài khoản Zalo chưa sẵn sàng',
                description: 'Vui lòng kiểm tra hoặc đăng nhập lại tài khoản trước khi tạo chiến dịch.'
            });
            return;
        }
        if (recipientSource === 'phones' && phonePreview && phonePreview.invalid_count > 0) {
            notification.warning({ message: 'Vui lòng sửa các số điện thoại không hợp lệ' });
            return;
        }
        if (imageFiles.some(file => file.status === 'uploading')) {
            notification.warning({ message: 'Vui lòng chờ ảnh tải lên hoàn tất' });
            return;
        }
        if (imageFiles.some(file => file.status === 'error')) {
            notification.warning({ message: 'Có ảnh tải lên thất bại, vui lòng xóa hoặc tải lại' });
            return;
        }
        const attachments = imageFiles
            .filter(file => file.status === 'done' && file.response && file.response.attachment)
            .map(file => file.response.attachment);
        this.setState({ creating: true });
        try {
            await fetchData({
                url: 'api/v1/zalo-campaigns',
                method: 'post',
                data: {
                    ...values,
                    recipient_source: recipientSource,
                    send_all_friends: recipientSource === 'friends' && sendAllFriends,
                    friend_ids: recipientSource === 'friends' && !sendAllFriends ? values.friend_ids : [],
                    attachments,
                    scheduled_at: scheduleEnabled && values.scheduled_at
                        ? values.scheduled_at.toISOString()
                        : null,
                    send_friend_request: friendEnabled,
                    friend_request_message: friendEnabled ? values.friend_request_message : ''
                }
            });
            notification.success({ message: 'Đã tạo chiến dịch gửi tin Zalo' });
            this.setState({
                creating: false,
                formVisible: false,
                phonePreview: null,
                scheduleEnabled: false,
                friendEnabled: false,
                recipientSource: 'phones',
                selectedChannelId: '',
                zaloFriends: [],
                sendAllFriends: true,
                imageFiles: [],
                page: 1
            }, this.loadCampaigns);
            this.formRef.current.resetFields();
        } catch (err) {
            this.setState({ creating: false });
            notification.error({
                message: 'Không thể tạo chiến dịch',
                description: err.message || (err.err && err.err.message) || 'Vui lòng kiểm tra lại dữ liệu'
            });
        }
    };

    cancelCampaign = async id => {
        try {
            await fetchData({
                url: `api/v1/zalo-campaigns/${id}/cancel`,
                method: 'post',
                data: {}
            });
            notification.success({ message: 'Đã hủy chiến dịch' });
            this.loadCampaigns();
            if (this.state.selectedCampaign && this.state.selectedCampaign._id === id) {
                this.setState({ detailVisible: false });
            }
        } catch (err) {
            notification.error({ message: err.message || 'Không thể hủy chiến dịch' });
        }
    };

    openDetail = async campaign => {
        this.setState({
            selectedCampaign: campaign,
            detailVisible: true,
            targetPage: 1
        }, this.loadTargets);
    };

    loadTargets = async (showLoading = true) => {
        const { selectedCampaign, targetPage, limit } = this.state;
        if (!selectedCampaign) return;
        if (showLoading) this.setState({ detailLoading: true });
        try {
            const [campaignResponse, targetResponse] = await Promise.all([
                fetchData({ url: `api/v1/zalo-campaigns/${selectedCampaign._id}` }),
                fetchData({
                    url: `api/v1/zalo-campaigns/${selectedCampaign._id}/targets?page=${targetPage}&limit=${limit}`
                })
            ]);
            this.setState({
                selectedCampaign: campaignResponse.data || selectedCampaign,
                targets: targetResponse.data || [],
                targetTotal: targetResponse.total || 0,
                detailLoading: false
            });
        } catch (err) {
            this.setState({ detailLoading: false });
        }
    };

    renderStatus = value => {
        const status = STATUS[value] || { color: 'default', text: value };
        return <Tag color={status.color}>{status.text}</Tag>;
    };

    renderActionStatus = value => {
        const status = ACTION_STATUS[value] || { color: 'default', text: value };
        return <Tag color={status.color}>{status.text}</Tag>;
    };

    render() {
        const {
            campaigns, total, page, limit, loading, channels, formVisible, creating,
            phonePreview, previewLoading, scheduleEnabled, friendEnabled, detailVisible,
            selectedCampaign, targets, targetTotal, targetPage, detailLoading,
            recipientSource, zaloFriends, friendsLoading, sendAllFriends, imageFiles,
            selectedChannelId, selectedChannelSession, channelSessionChecking,
            reconnectVisible, reconnectChannel
        } = this.state;

        const selectedChannel = this.getChannel(selectedChannelId);
        const detailChannel = selectedCampaign && this.getChannel(selectedCampaign.channel_id);
        const detailHasSessionError = selectedCampaign && (
            /zpw_sek|zpsid|phiên đăng nhập zalo.*hết hạn|phiên zalo.*hết hạn/i.test(selectedCampaign.last_error || '') ||
            targets.some(target => /zpw_sek|zpsid|phiên đăng nhập zalo.*hết hạn|phiên zalo.*hết hạn/i.test(
                target.message_error || target.friend_request_error || ''
            ))
        );
        const detailSessionExpired = detailHasSessionError && (
            !detailChannel || !detailChannel.zalo_credentials || detailChannel.zalo_credentials.session_status !== 'active'
        );

        const columns = [
            {
                title: 'Chiến dịch',
                dataIndex: 'name',
                render: (name, record) => (
                    <div>
                        <div style={{ fontWeight: 600 }}>{name}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                            {moment(record.created_time).format('HH:mm DD/MM/YYYY')}
                        </div>
                    </div>
                )
            },
            {
                title: 'Tài khoản gửi',
                dataIndex: 'channel_name',
                render: (name, record) => (
                    <Space>
                        <Avatar size={30} src={record.channel_avatar}>{(name || 'Z').slice(0, 1)}</Avatar>
                        <span>{name}</span>
                    </Space>
                )
            },
            {
                title: 'Thời gian thực hiện',
                dataIndex: 'scheduled_at',
                width: 165,
                render: value => moment(value).format('HH:mm DD/MM/YYYY')
            },
            {
                title: 'Trạng thái',
                dataIndex: 'status',
                width: 150,
                render: this.renderStatus
            },
            {
                title: 'Tiến độ',
                width: 190,
                render: (_, record) => {
                    const done = record.success_count + record.failed_count;
                    const percent = record.total_targets
                        ? Math.round(done * 100 / record.total_targets)
                        : 0;
                    return (
                        <div>
                            <Progress percent={percent} size="small" status={record.status === 'failed' ? 'exception' : 'normal'} />
                            <div style={{ fontSize: 12 }}>
                                {record.success_count} thành công · {record.failed_count} lỗi
                            </div>
                        </div>
                    );
                }
            },
            {
                title: 'Kết bạn',
                dataIndex: 'send_friend_request',
                width: 100,
                render: value => value
                    ? <Tag color="cyan"><TeamOutlined /> Có</Tag>
                    : <Tag>Không</Tag>
            },
            {
                title: '',
                width: 90,
                fixed: 'right',
                render: (_, record) => (
                    <Space>
                        <Tooltip title="Xem chi tiết">
                            <Button icon={<EyeOutlined />} size="small" onClick={() => this.openDetail(record)} />
                        </Tooltip>
                        {['scheduled', 'running'].includes(record.status) && (
                            <Popconfirm
                                title="Hủy chiến dịch này?"
                                okText="Hủy chiến dịch"
                                cancelText="Đóng"
                                onConfirm={() => this.cancelCampaign(record._id)}
                            >
                                <Tooltip title="Hủy chiến dịch">
                                    <Button icon={<StopOutlined />} danger size="small" />
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </Space>
                )
            }
        ];

        const targetColumns = [
            { title: 'Số điện thoại', dataIndex: 'phone_masked', width: 130 },
            { title: 'Tên Zalo', dataIndex: 'zalo_name', render: value => value || '—' },
            {
                title: 'Kết bạn',
                dataIndex: 'friend_request_status',
                render: this.renderActionStatus
            },
            {
                title: 'Tin nhắn',
                dataIndex: 'message_status',
                render: value => this.renderActionStatus(value)
            },
            {
                title: 'Lỗi',
                render: (_, record) => record.message_error || record.friend_request_error || '—'
            }
        ];

        return (
            <div className="content-m zalo-campaign-page">
                <Card bordered={false} className="zalo-campaign-shell">
                    <div className="zalo-campaign-toolbar">
                        <div>
                            <h3>Gửi tin Zalo</h3>
                            <div className="zalo-campaign-subtitle">
                                Quản lý và theo dõi các chiến dịch gửi tin theo số điện thoại hoặc bạn bè Zalo.
                            </div>
                        </div>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={() => this.loadCampaigns()}>
                                Làm mới
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => this.setState({ formVisible: true })}
                            >
                                Thêm chiến dịch
                            </Button>
                        </Space>
                    </div>

                    <div className="zalo-campaign-filters">
                        <Input.Search
                            allowClear
                            placeholder="Tìm tên chiến dịch"
                            onChange={event => {
                                const keyword = event.target.value;
                                if (this.searchTimer) clearTimeout(this.searchTimer);
                                this.searchTimer = setTimeout(() => this.setState({ keyword, page: 1 }, this.loadCampaigns), 350);
                            }}
                        />
                        <Select
                            allowClear
                            placeholder="Trạng thái"
                            onChange={status => this.setState({ status: status || '', page: 1 }, this.loadCampaigns)}
                        >
                            {Object.keys(STATUS).map(key => (
                                <Select.Option key={key} value={key}>{STATUS[key].text}</Select.Option>
                            ))}
                        </Select>
                    </div>

                    <Table
                        className="zalo-campaign-table"
                        rowKey="_id"
                        columns={columns}
                        dataSource={campaigns}
                        loading={loading}
                        pagination={false}
                        scroll={{ x: 1150 }}
                        locale={{ emptyText: <Empty description="Chưa có chiến dịch gửi tin Zalo" /> }}
                    />
                    {total > limit && (
                        <Pagination
                            className="zalo-campaign-pagination"
                            current={page}
                            pageSize={limit}
                            total={total}
                            onChange={nextPage => this.setState({ page: nextPage }, this.loadCampaigns)}
                        />
                    )}
                </Card>

                <Modal
                    className="zalo-campaign-modal"
                    title={(
                        <div className="zalo-campaign-modal-title">
                            <span className="zalo-campaign-modal-title-icon"><MessageOutlined /></span>
                            <span>
                                <strong>Thêm chiến dịch gửi tin Zalo</strong>
                                <small>Thiết lập nội dung, người nhận và thời gian thực hiện</small>
                            </span>
                        </div>
                    )}
                    visible={formVisible}
                    width={900}
                    centered
                    destroyOnClose
                    maskClosable={!creating}
                    onCancel={this.closeCampaignForm}
                    footer={[
                        <Button key="close" onClick={this.closeCampaignForm} disabled={creating}>
                            Đóng
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            htmlType="submit"
                            form="zalo-campaign-form"
                            loading={creating}
                            disabled={channelSessionChecking || (!!selectedChannelId && (!selectedChannelSession || selectedChannelSession.status !== 'active'))}
                            icon={<MessageOutlined />}
                        >
                            Tạo chiến dịch
                        </Button>
                    ]}
                >
                    <Form
                        id="zalo-campaign-form"
                        className="zalo-campaign-form"
                        ref={this.formRef}
                        layout="vertical"
                        onFinish={this.createCampaign}
                        initialValues={{
                            name: `Gửi tin Zalo ${moment().format('DD/MM')}`,
                            friend_request_message: 'Xin chào, mình muốn kết bạn với bạn!'
                        }}
                    >
                        <section className="zalo-campaign-section">
                            <div className="zalo-campaign-section-heading">
                                <span>Thiết lập chiến dịch</span>
                                <small>Thông tin chung và tài khoản thực hiện</small>
                            </div>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="name"
                                        label="Tên chiến dịch"
                                        rules={[{ required: true, message: 'Vui lòng nhập tên chiến dịch' }]}
                                    >
                                        <Input placeholder="Ví dụ: Chăm sóc khách hàng tháng 7" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="channel_id"
                                        label="Tài khoản Zalo gửi"
                                        rules={[{ required: true, message: 'Vui lòng chọn tài khoản Zalo' }]}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="children"
                                            placeholder="Chọn tài khoản đã kết nối"
                                            loading={channelSessionChecking}
                                            notFoundContent="Chưa có tài khoản Zalo"
                                            onChange={channelId => {
                                                this.setState({
                                                    selectedChannelId: channelId,
                                                    selectedChannelSession: null,
                                                    zaloFriends: []
                                                });
                                                if (this.formRef.current) {
                                                    this.formRef.current.setFieldsValue({ friend_ids: [] });
                                                }
                                                if (recipientSource === 'friends') this.loadFriends(channelId);
                                                this.checkSelectedChannel(channelId);
                                            }}
                                        >
                                            {channels.map(channel => (
                                                <Select.Option
                                                    key={channel._id}
                                                    value={channel._id}
                                                >
                                                    {channel.full_name || channel.username || channel.user_id}
                                                    {channel.zalo_credentials && channel.zalo_credentials.session_status === 'expired' ? ' — Phiên đã hết hạn' : ''}
                                                    {channel.zalo_credentials && channel.zalo_credentials.session_status === 'error' ? ' — Lỗi kiểm tra phiên' : ''}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            {channelSessionChecking && (
                                <Alert
                                    className="zalo-campaign-session-alert"
                                    type="info"
                                    showIcon
                                    message="Đang kiểm tra phiên đăng nhập Zalo..."
                                />
                            )}
                            {!channelSessionChecking && selectedChannelSession && selectedChannelSession.status !== 'active' && (
                                <Alert
                                    className="zalo-campaign-session-alert"
                                    type={selectedChannelSession.status === 'expired' ? 'error' : 'warning'}
                                    showIcon
                                    message={selectedChannelSession.status === 'expired'
                                        ? 'Tài khoản Zalo đã hết hạn đăng nhập'
                                        : 'Chưa thể xác thực phiên Zalo'}
                                    description={selectedChannelSession.status === 'expired'
                                        ? 'Quét mã QR để kết nối lại, sau đó bạn có thể tiếp tục tạo chiến dịch.'
                                        : (selectedChannelSession.error || selectedChannelSession.message)}
                                    action={(
                                        <Button
                                            type="primary"
                                            icon={<LoginOutlined />}
                                            onClick={() => this.openReconnect(selectedChannel)}
                                        >
                                            Đăng nhập lại Zalo
                                        </Button>
                                    )}
                                />
                            )}
                        </section>

                        <section className="zalo-campaign-section">
                            <div className="zalo-campaign-section-heading">
                                <span>Nội dung gửi</span>
                                <small>Tin nhắn và hình ảnh khách hàng sẽ nhận được</small>
                            </div>
                            <Row gutter={18}>
                                <Col xs={24} md={16}>
                                    <Form.Item
                                        name="content"
                                        label="Nội dung tin nhắn"
                                        rules={[{ required: true, message: 'Vui lòng nhập nội dung tin nhắn' }]}
                                    >
                                        <Input.TextArea
                                            rows={4}
                                            maxLength={2000}
                                            showCount
                                            placeholder="Nhập nội dung gửi cho khách hàng"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Ảnh đính kèm" className="zalo-campaign-upload">
                                        <Upload
                                            action={`${process.env.REACT_APP_API_CHOTSALE_URL || window.location.origin}/api/v1/uploads`}
                                            headers={{
                                                Authorization: `${AccountMetaData.getToken()}`,
                                                Company: AccountMetaData.getCompanyId()
                                            }}
                                            withCredentials
                                            accept="image/*"
                                            listType="picture-card"
                                            fileList={imageFiles}
                                            maxCount={3}
                                            beforeUpload={file => {
                                                if (!file.type.startsWith('image/')) {
                                                    notification.error({ message: 'Chỉ được tải lên tệp hình ảnh' });
                                                    return Upload.LIST_IGNORE;
                                                }
                                                if (file.size > 25 * 1000 * 1000) {
                                                    notification.error({ message: 'Mỗi ảnh không được vượt quá 25MB' });
                                                    return Upload.LIST_IGNORE;
                                                }
                                                return true;
                                            }}
                                            onChange={({ fileList }) => this.setState({ imageFiles: fileList.slice(-3) })}
                                            onPreview={file => {
                                                const url = file.url || (file.response && file.response.attachment && file.response.attachment.url);
                                                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                            }}
                                        >
                                            {imageFiles.length < 3 && (
                                                <div><PictureOutlined /><div>Thêm ảnh</div></div>
                                            )}
                                        </Upload>
                                        <div className="zalo-campaign-field-hint">Tối đa 3 ảnh, mỗi ảnh không quá 25MB.</div>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        <section className="zalo-campaign-section">
                            <div className="zalo-campaign-section-heading zalo-campaign-recipient-heading">
                                <span>
                                    <span>Người nhận</span>
                                    <small>Chọn nguồn và danh sách khách hàng cần gửi</small>
                                </span>
                                <Radio.Group
                                    className="zalo-campaign-source"
                                    value={recipientSource}
                                    onChange={this.changeRecipientSource}
                                >
                                    <Radio.Button value="phones">Danh sách số điện thoại</Radio.Button>
                                    <Radio.Button value="friends">Bạn bè của nick Zalo</Radio.Button>
                                </Radio.Group>
                            </div>

                            {recipientSource === 'phones' ? (
                                <>
                                    <Form.Item
                                        name="phones"
                                        label="Danh sách số điện thoại — mỗi dòng một số"
                                        rules={[{ required: true, message: 'Vui lòng nhập danh sách số điện thoại' }]}
                                    >
                                        <Input.TextArea
                                            className="zalo-campaign-phone-input"
                                            rows={5}
                                            placeholder={'0901234567\n0912345678\n+84921234567'}
                                            onChange={this.handlePhonesChange}
                                        />
                                    </Form.Item>

                                    <Spin spinning={previewLoading}>
                                        {phonePreview && (
                                            <Alert
                                                className="zalo-campaign-preview"
                                                type={phonePreview.invalid_count ? 'warning' : 'success'}
                                                showIcon
                                                message={`${phonePreview.valid_count} hợp lệ · ${phonePreview.duplicates} trùng · ${phonePreview.invalid_count} không hợp lệ`}
                                                description={phonePreview.invalid_count
                                                    ? `Dòng cần kiểm tra: ${phonePreview.invalid.map(item => item.line).join(', ')}`
                                                    : 'Danh sách số điện thoại đã sẵn sàng.'}
                                            />
                                        )}
                                    </Spin>
                                </>
                            ) : (
                                <div className="zalo-campaign-friend-picker">
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Checkbox
                                            checked={sendAllFriends}
                                            onChange={event => this.setState({ sendAllFriends: event.target.checked })}
                                        >
                                            Gửi cho tất cả bạn bè đã đồng bộ ({zaloFriends.length})
                                        </Checkbox>
                                        {!sendAllFriends && (
                                            <Form.Item
                                                name="friend_ids"
                                                label="Chọn bạn bè nhận tin"
                                                style={{ marginBottom: 0 }}
                                                rules={[{ required: true, message: 'Vui lòng chọn ít nhất một người' }]}
                                            >
                                                <Select
                                                    mode="multiple"
                                                    showSearch
                                                    optionFilterProp="children"
                                                    loading={friendsLoading}
                                                    maxTagCount="responsive"
                                                    placeholder="Chọn bạn bè Zalo"
                                                >
                                                    {zaloFriends.map(friend => (
                                                        <Select.Option key={friend.user_id} value={friend.user_id}>
                                                            {friend.display_name || friend.user_id}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        )}
                                        {!friendsLoading && zaloFriends.length === 0 && (
                                            <Alert type="warning" showIcon message="Nick Zalo chưa có danh sách bạn bè đã đồng bộ." />
                                        )}
                                    </Space>
                                </div>
                            )}
                        </section>

                        <section className="zalo-campaign-section">
                            <div className="zalo-campaign-section-heading">
                                <span>Tùy chọn gửi tin</span>
                                <small>Các hành động đi kèm và thời gian bắt đầu</small>
                            </div>
                            <div className="zalo-campaign-option-grid">
                                {recipientSource === 'phones' && (
                                    <div className={`zalo-campaign-option-card${friendEnabled ? ' is-active' : ''}`}>
                                        <div className="zalo-campaign-option-header">
                                            <span className="zalo-campaign-option-icon"><TeamOutlined /></span>
                                            <span className="zalo-campaign-option-copy">
                                                <strong>Gửi lời mời kết bạn</strong>
                                                <small>Thực hiện trước khi gửi tin nhắn</small>
                                            </span>
                                            <Switch
                                                checked={friendEnabled}
                                                onChange={value => this.setState({ friendEnabled: value })}
                                            />
                                        </div>
                                        {friendEnabled && (
                                            <Form.Item
                                                name="friend_request_message"
                                                label="Nội dung lời mời"
                                                className="zalo-campaign-option-field"
                                                rules={[{ required: true, message: 'Vui lòng nhập nội dung lời mời' }]}
                                            >
                                                <Input maxLength={150} showCount />
                                            </Form.Item>
                                        )}
                                    </div>
                                )}
                                <div className={`zalo-campaign-option-card${scheduleEnabled ? ' is-active' : ''}`}>
                                    <div className="zalo-campaign-option-header">
                                        <span className="zalo-campaign-option-icon"><ClockCircleOutlined /></span>
                                        <span className="zalo-campaign-option-copy">
                                            <strong>Hẹn thời gian thực hiện</strong>
                                            <small>Mặc định chiến dịch bắt đầu ngay</small>
                                        </span>
                                        <Switch
                                            checked={scheduleEnabled}
                                            onChange={value => this.setState({ scheduleEnabled: value })}
                                        />
                                    </div>
                                    {scheduleEnabled && (
                                        <Form.Item
                                            name="scheduled_at"
                                            className="zalo-campaign-option-field"
                                            rules={[{ required: true, message: 'Vui lòng chọn thời gian thực hiện' }]}
                                        >
                                            <DatePicker
                                                showTime
                                                format="HH:mm DD/MM/YYYY"
                                                style={{ width: '100%' }}
                                                disabledDate={current => current && current < moment().startOf('day')}
                                                placeholder="Chọn ngày và giờ gửi"
                                            />
                                        </Form.Item>
                                    )}
                                </div>
                            </div>
                        </section>

                        <Alert
                            className="zalo-campaign-notice"
                            type="info"
                            showIcon
                            message="Hệ thống gửi tuần tự và có khoảng nghỉ giữa các số để hạn chế Zalo rate limit."
                        />
                    </Form>
                </Modal>

                <Modal
                    visible={reconnectVisible}
                    centered
                    width={520}
                    footer={null}
                    destroyOnClose
                    onCancel={() => this.setState({ reconnectVisible: false, reconnectChannel: null })}
                >
                    {reconnectChannel && (
                        <ZaloConnect
                            companyId={this.props.UserLogin?.company_id || this.props.companySelected?.company_id}
                            reconnectChannelId={reconnectChannel._id}
                            reconnectChannelName={reconnectChannel.full_name || reconnectChannel.channel_name}
                            onSuccess={this.handleReconnectSuccess}
                            onCancel={() => this.setState({ reconnectVisible: false, reconnectChannel: null })}
                        />
                    )}
                </Modal>

                <Drawer
                    title={selectedCampaign ? `Chi tiết: ${selectedCampaign.name}` : 'Chi tiết chiến dịch'}
                    visible={detailVisible}
                    width={typeof window !== 'undefined' && window.innerWidth < 1065 ? '92vw' : 980}
                    onClose={() => this.setState({ detailVisible: false })}
                >
                    {selectedCampaign && (
                        <>
                            {detailSessionExpired && (
                                <Alert
                                    className="zalo-campaign-session-alert"
                                    type="error"
                                    showIcon
                                    message="Phiên đăng nhập Zalo của chiến dịch đã hết hạn"
                                    description="Đăng nhập lại tài khoản gửi để có thể tạo và thực hiện chiến dịch tiếp theo."
                                    action={(
                                        <Button
                                            type="primary"
                                            icon={<LoginOutlined />}
                                            onClick={() => this.openReconnect(
                                                this.getChannel(selectedCampaign.channel_id) || {
                                                    _id: selectedCampaign.channel_id,
                                                    full_name: selectedCampaign.channel_name
                                                }
                                            )}
                                        >
                                            Đăng nhập lại Zalo
                                        </Button>
                                    )}
                                />
                            )}
                            <Row gutter={12} style={{ marginBottom: 18 }}>
                                <Col span={6}><Card size="small"><Statistic title="Tổng số" value={selectedCampaign.total_targets} /></Card></Col>
                                <Col span={6}><Card size="small"><Statistic title="Gửi thành công" value={selectedCampaign.success_count} valueStyle={{ color: '#3f8600' }} /></Card></Col>
                                <Col span={6}><Card size="small"><Statistic title="Gửi thất bại" value={selectedCampaign.failed_count} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                                <Col span={6}><Card size="small"><Statistic title="Kết bạn thành công" value={selectedCampaign.friend_success_count} /></Card></Col>
                            </Row>
                            <Descriptions bordered size="small" column={2} style={{ marginBottom: 18 }}>
                                <Descriptions.Item label="Tài khoản">{selectedCampaign.channel_name}</Descriptions.Item>
                                <Descriptions.Item label="Trạng thái">{this.renderStatus(selectedCampaign.status)}</Descriptions.Item>
                                <Descriptions.Item label="Thời gian">{moment(selectedCampaign.scheduled_at).format('HH:mm DD/MM/YYYY')}</Descriptions.Item>
                                <Descriptions.Item label="Nguồn">{selectedCampaign.recipient_source === 'friends' ? 'Bạn bè Zalo' : 'Số điện thoại'}</Descriptions.Item>
                                <Descriptions.Item label="Kết bạn">{selectedCampaign.send_friend_request ? 'Có' : 'Không'}</Descriptions.Item>
                                <Descriptions.Item label="Tin nhắn" span={2}>{selectedCampaign.content}</Descriptions.Item>
                                {selectedCampaign.attachments && selectedCampaign.attachments.length > 0 && (
                                    <Descriptions.Item label="Ảnh đính kèm" span={2}>
                                        <Space wrap>
                                            {selectedCampaign.attachments.map(file => (
                                                <a key={file.url || file.name} href={file.url} target="_blank" rel="noreferrer">
                                                    <img
                                                        src={file.url}
                                                        alt={file.name}
                                                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4 }}
                                                    />
                                                </a>
                                            ))}
                                        </Space>
                                    </Descriptions.Item>
                                )}
                                {selectedCampaign.send_friend_request && (
                                    <Descriptions.Item label="Lời mời kết bạn" span={2}>{selectedCampaign.friend_request_message}</Descriptions.Item>
                                )}
                                {selectedCampaign.last_error && (
                                    <Descriptions.Item label="Lỗi chiến dịch" span={2}>{selectedCampaign.last_error}</Descriptions.Item>
                                )}
                            </Descriptions>
                            <Table
                                rowKey="_id"
                                size="small"
                                columns={targetColumns}
                                dataSource={targets}
                                loading={detailLoading}
                                pagination={false}
                                scroll={{ x: 760 }}
                            />
                            {targetTotal > limit && (
                                <Pagination
                                    style={{ marginTop: 18, textAlign: 'right' }}
                                    current={targetPage}
                                    pageSize={limit}
                                    total={targetTotal}
                                    onChange={nextPage => this.setState({ targetPage: nextPage }, this.loadTargets)}
                                />
                            )}
                        </>
                    )}
                </Drawer>
            </div>
        );
    }
}
